import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import { Supplier, SupplierCreate, SupplierUpdate, SupplierAttachment } from '../models/Supplier';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface SupplierPagedResult {
  items: Supplier[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class SuppliersService {
  private readonly baseUrl = environment.microservices.shopping;
  private get blobBaseUrl() { return `${this.baseUrl}/blobsupplier`; }

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  getAllPaged(
    page: number = 1, 
    pageSize: number = 50,
    filters?: {
      status?: string;
      category?: string;
      state?: string;
      search?: string;
    }
  ): Observable<SupplierPagedResult> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (filters) {
      if (filters.status) params = params.set('status', filters.status);
      if (filters.category) params = params.set('category', filters.category);
      if (filters.state) params = params.set('state', filters.state);
      if (filters.search) params = params.set('search', filters.search);
    }

    return this.http.get<any>(`${this.baseUrl}/suppliers`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedores'); return throwError(() => err); })
    );
  }

  getAll(): Observable<Supplier[]> {
    return this.getAllPaged().pipe(map(r => r.items));
  }

  getById(id: number): Observable<Supplier> {
    return this.http.get<any>(`${this.baseUrl}/suppliers/${id}`).pipe(
      map(dto => this.mapFromSupplierDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedor'); return throwError(() => err); })
    );
  }

  create(payload: SupplierCreate): Observable<Supplier> {
    const url = `${this.baseUrl}/suppliers`;
    const dto = this.mapToSupplierDto(payload);
    // Logs auxiliares para depuração
    // eslint-disable-next-line no-console
    return this.http.post<Supplier>(url, dto).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao criar fornecedor'); return throwError(() => err); })
    );
  }

  update(id: number, payload: SupplierUpdate): Observable<void> {
    const dto = this.mapToSupplierDto(payload);
    // eslint-disable-next-line no-console
    return this.http.put<void>(`${this.baseUrl}/suppliers/${id}`, dto).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar fornecedor'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/suppliers/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao excluir fornecedor'); return throwError(() => err); })
    );
  }

  getByCnpj(cnpj: string): Observable<Supplier> {
    return this.http.get<any>(`${this.baseUrl}/suppliers/cnpj/${encodeURIComponent(cnpj)}`).pipe(
      map(dto => this.mapFromSupplierDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedor por CNPJ'); return throwError(() => err); })
    );
  }

  getByDocument(documentNumber: string): Observable<Supplier | null> {
    return this.http.get<any>(`${this.baseUrl}/suppliers/document/${encodeURIComponent(documentNumber)}`).pipe(
      map(dto => {
        if (dto == null || dto === '') {
          return null;
        }
        if (typeof dto === 'object' && !Array.isArray(dto) && Object.keys(dto).length === 0) {
          return null;
        }
        const supplier = this.mapFromSupplierDto(dto);
        // API às vezes responde 200 com corpo vazio / sem Id: o map vira id 0 — não é fornecedor real.
        const id = supplier.id;
        if (id == null || Number(id) <= 0) {
          return null;
        }
        return supplier;
      }),
      catchError(err => {
        const status = err?.status;
        if (status === 404) {
          return of(null);
        }
        this.errorService.handleHttpError(err, 'Erro ao buscar fornecedor por documento');
        return throwError(() => err);
      })
    );
  }

  getByEmail(email: string): Observable<Supplier> {
    return this.http.get<any>(`${this.baseUrl}/suppliers/email/${encodeURIComponent(email)}`).pipe(
      map(dto => this.mapFromSupplierDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedor por e-mail'); return throwError(() => err); })
    );
  }

  getByCategoryPaged(category: string, page: number = 1, pageSize: number = 50): Observable<SupplierPagedResult> {
    const params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());
    return this.http.get<any>(`${this.baseUrl}/suppliers/category/${encodeURIComponent(category)}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedores por categoria'); return throwError(() => err); })
    );
  }

  getByCategory(category: string, page: number = 1, pageSize: number = 50): Observable<Supplier[]> {
    return this.getByCategoryPaged(category, page, pageSize).pipe(map(r => r.items));
  }

  getActivePaged(page: number = 1, pageSize: number = 50): Observable<SupplierPagedResult> {
    const params = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());
    return this.http.get<any>(`${this.baseUrl}/suppliers/active`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar fornecedores ativos'); return throwError(() => err); })
    );
  }

  getActive(page: number = 1, pageSize: number = 50): Observable<Supplier[]> {
    return this.getActivePaged(page, pageSize).pipe(map(r => r.items));
  }

  block(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/suppliers/${id}/block`, {}).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao bloquear fornecedor'); return throwError(() => err); })
    );
  }

  unblock(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/suppliers/${id}/unblock`, {}).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao desbloquear fornecedor'); return throwError(() => err); })
    );
  }

  activate(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/suppliers/${id}/activate`, {}).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao ativar fornecedor'); return throwError(() => err); })
    );
  }

  deactivate(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/suppliers/${id}/deactivate`, {}).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao desativar fornecedor'); return throwError(() => err); })
    );
  }

  // Attachments
  listAttachments(supplierId: number, opts?: { category?: string; activeOnly?: boolean }): Observable<SupplierAttachment[]> {
    return this.http.get<any>(`${this.baseUrl}/supplier/${supplierId}/attachments`).pipe(
      map(response => response.attachments || []),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao listar anexos do fornecedor'); return throwError(() => err); })
    );
  }

  uploadAttachment(supplierId: number, file: File, meta?: { category?: string; description?: string }): Observable<SupplierAttachment> {
    // 1) Envia para BlobFunctions e obtém URL/nome
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders();

    type BlobUploadResponse = { success: boolean; url: string; fileName: string; container: string };
    const prefix = `supplier_${supplierId}`;

    const uploadUrl = `${this.blobBaseUrl}/upload?prefix=${encodeURIComponent(prefix)}`;
    // eslint-disable-next-line no-console
    return this.http.post<BlobUploadResponse>(uploadUrl, formData, { headers }).pipe(
      switchMap((res) => {
        if (!res?.success || !res.url) {
          throw new Error('Falha no upload para Blob');
        }

        // 2) Registra o anexo no backend de fornecedores usando nova rota
        const payload = {
          blobUrl: res.url,
          originalFileName: file.name,
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size,
          category: meta?.category,
          description: meta?.description,
          uploadedBy: 'system'
        };

        return this.registerAttachment(supplierId, payload);
      }),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao enviar anexo do fornecedor'); return throwError(() => err); })
    );
  }

  private registerAttachment(supplierId: number, payload: any): Observable<SupplierAttachment> {
    // Usa nova rota atualizada: POST /supplier/{supplierId}/attachments/register
    return this.http.post<any>(`${this.baseUrl}/supplier/${supplierId}/attachments/register`, payload).pipe(
      map(response => response.attachment),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao registrar anexo do fornecedor'); return throwError(() => err); })
    );
  }

  // Método getAttachment removido - não há rota correspondente no backend

  deactivateAttachment(supplierId: number, attachmentId: number): Observable<void> {
    return this.http.delete<any>(`${this.baseUrl}/supplier/attachments/${attachmentId}`).pipe(
      map(response => {
        if (!response.success) {
          throw new Error('Failed to deactivate attachment');
        }
      }),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao desativar anexo do fornecedor'); return throwError(() => err); })
    );
  }

  removeAttachment(supplierId: number, attachmentId: number): Observable<void> {
    return this.deactivateAttachment(supplierId, attachmentId);
  }

  getAttachmentStatistics(supplierId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/supplier/${supplierId}/attachments/statistics`).pipe(
      map(response => response.statistics),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar estatísticas de anexos'); return throwError(() => err); })
    );
  }

  /**
   * Mapeia resposta paginada (Items/items em raiz ou dentro de data/result, etc.)
   */
  private mapPagedResponse(response: any): SupplierPagedResult {
    const { rawItems, meta } = this.extractSupplierPagedPayload(response);
    const items = this.mapListFromDto(rawItems);

    const page = meta.Page ?? meta.page ?? 1;
    const pageSize = meta.PageSize ?? meta.pageSize ?? (items.length || 50);
    const totalItems =
      meta.TotalItems ?? meta.totalItems ?? meta.TotalRecords ?? meta.totalRecords ?? items.length;
    const totalPages =
      meta.TotalPages ?? meta.totalPages ?? (pageSize > 0 ? Math.ceil(Number(totalItems) / pageSize) : 1);

    return { items, page, pageSize, totalItems, totalPages };
  }

  /**
   * Extrai lista e metadados de paginação mesmo quando a API envolve o payload
   * (ex.: `{ data: { items, page, totalItems } }`).
   */
  private extractSupplierPagedPayload(response: any): { rawItems: any[]; meta: any } {
    if (!response) {
      return { rawItems: [], meta: {} };
    }
    if (Array.isArray(response)) {
      return { rawItems: response, meta: {} };
    }
    if (typeof response !== 'object') {
      return { rawItems: [], meta: {} };
    }

    const pickArray = (o: any): any[] | null => {
      if (!o || typeof o !== 'object') return null;
      if (Array.isArray(o.Items)) return o.Items;
      if (Array.isArray(o.items)) return o.items;
      if (Array.isArray(o.data)) return o.data;
      if (Array.isArray(o.Data)) return o.Data;
      return null;
    };

    const unwrap = (o: any): any =>
      o?.data ?? o?.Data ?? o?.result ?? o?.Result ?? o?.payload ?? o?.Payload ?? o;

    const tryBag = (bag: any): { rawItems: any[]; meta: any } | null => {
      if (!bag || typeof bag !== 'object') return null;
      const arr = pickArray(bag);
      if (arr !== null) {
        return { rawItems: arr, meta: bag };
      }
      return null;
    };

    const inner = unwrap(response);
    const fromInner = tryBag(inner);
    if (fromInner) {
      return fromInner;
    }
    if (Array.isArray(inner)) {
      return { rawItems: inner, meta: {} };
    }

    const fromRoot = tryBag(response);
    if (fromRoot) {
      return fromRoot;
    }

    return { rawItems: [], meta: {} };
  }

  // Mapeia o payload do formulário para o SupplierDTO esperado pelo backend
  private mapToSupplierDto(payload: any): any {
    const status = payload?.active === false ? 'Inativo' : 'Ativo';
    return {
      // Requeridos no DTO
      PersonType: payload?.personType || 'Juridica',
      CompanyName: payload?.companyName ?? '',
      DocumentNumber: payload?.cnpj ?? payload?.documentNumber ?? '',
      Status: status,

      // Opcionais no DTO (enviar quando existirem)
      TradeName: payload?.tradeName,
      StateRegistration: payload?.stateRegistration,
      FoundationDate: payload?.foundationDate || null,
      BusinessSegment: payload?.businessSegment,
      Email: payload?.email,
      Website: payload?.website,
      PaymentTerms: payload?.paymentTerms,
      CreditLimit: payload?.creditLimit,
      PaymentDays: payload?.paymentDays,
      BankName: payload?.bankName,
      Agency: payload?.agency,
      AccountType: payload?.accountType,
      AccountNumber: payload?.accountNumber,
      AccountHolderName: payload?.accountHolderName,
      PIXKey: payload?.pixKey,
      SupplierCategory: payload?.supplierCategory,
      Notes: payload?.notes,

      // Coleções (preenche a partir do formulário)
      Addresses: [
        {
          SupplierId: 0,
          AddressType: payload?.addressType || 'Comercial',
          Street: payload?.street ?? '',
          Number: payload?.number ?? undefined,
          Complement: payload?.complement ?? undefined,
          Neighborhood: payload?.neighborhood ?? undefined,
          City: payload?.city ?? '',
          State: payload?.state ?? '',
          ZipCode: payload?.zipCode ?? '',
          Country: payload?.country ?? 'BR',
          IsDefault: true
        }
      ],
      Contacts: [
        {
          SupplierId: 0,
          ContactType: payload?.contactType || 'Principal',
          ContactName: payload?.contactPersonName || payload?.tradeName || payload?.companyName || 'Contato',
          PhoneNumber: payload?.primaryPhone ?? '',
          Email: payload?.contactPersonEmail || payload?.email || undefined,
          Relationship: payload?.contactPersonPosition || undefined,
          IsEmergencyContact: false,
          IsDefault: true
        }
      ],
      Attachments: []
    };
  }

  // Converte SupplierDTO (API) -> Supplier (frontend)
  private mapFromSupplierDto(dto: any): Supplier {
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);
    const pick = (obj: any, keys: string[]) => {
      for (const k of keys) {
        const v = obj?.[k];
        if (v !== undefined) return v;
      }
      return undefined;
    };

    const addresses = pick(dto, ['Addresses', 'addresses']) || [];
    const contacts = pick(dto, ['Contacts', 'contacts']) || [];

    const defaultAddress = Array.isArray(addresses) && addresses.length > 0
      ? (addresses.find((a: any) => pick(a, ['IsDefault', 'isDefault'])) || addresses[0])
      : undefined;
    const defaultContact = Array.isArray(contacts) && contacts.length > 0
      ? (contacts.find((c: any) => pick(c, ['IsDefault', 'isDefault'])) || contacts[0])
      : undefined;
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      companyName: pick(dto, ['CompanyName', 'companyName']) ?? '',
      tradeName: pick(dto, ['TradeName', 'tradeName']) ?? undefined,
      cnpj: pick(dto, ['DocumentNumber', 'documentNumber', 'cnpj']) ?? '',
      stateRegistration: pick(dto, ['StateRegistration', 'stateRegistration']) ?? undefined,
      foundationDate: toIso(pick(dto, ['FoundationDate', 'foundationDate'])),
      street: pick(defaultAddress || {}, ['Street', 'street']) ?? undefined,
      number: pick(defaultAddress || {}, ['Number', 'number']) ?? undefined,
      complement: pick(defaultAddress || {}, ['Complement', 'complement']) ?? undefined,
      neighborhood: pick(defaultAddress || {}, ['Neighborhood', 'neighborhood']) ?? undefined,
      city: pick(defaultAddress || {}, ['City', 'city']) ?? undefined,
      state: pick(defaultAddress || {}, ['State', 'state']) ?? undefined,
      zipCode: pick(defaultAddress || {}, ['ZipCode', 'zipCode']) ?? undefined,
      primaryPhone: pick(defaultContact || {}, ['PhoneNumber', 'phoneNumber']) ?? undefined,
      secondaryPhone: undefined,
      email: pick(dto, ['Email', 'email']) ?? pick(defaultContact || {}, ['Email', 'email']) ?? undefined,
      website: pick(dto, ['Website', 'website']) ?? undefined,
      contactPersonName: pick(defaultContact || {}, ['ContactName', 'contactName']) ?? undefined,
      contactPersonPhone: pick(defaultContact || {}, ['PhoneNumber', 'phoneNumber']) ?? undefined,
      contactPersonEmail: pick(defaultContact || {}, ['Email', 'email']) ?? undefined,
      contactPersonPosition: pick(defaultContact || {}, ['Relationship', 'relationship']) ?? undefined,
      paymentTerms: pick(dto, ['PaymentTerms', 'paymentTerms']) ?? undefined,
      creditLimit: pick(dto, ['CreditLimit', 'creditLimit']) ?? undefined,
      paymentDays: pick(dto, ['PaymentDays', 'paymentDays']) ?? undefined,
      active: (pick(dto, ['Status', 'status']) ?? 'Ativo') === 'Ativo',
      blocked: false,
      supplierCategory: pick(dto, ['SupplierCategory', 'supplierCategory']) ?? undefined,
      notes: pick(dto, ['Notes', 'notes']) ?? undefined
    };
  }

  private mapListFromDto(list: any[]): Supplier[] {
    if (!Array.isArray(list)) return [];
    return list.map(x => this.mapFromSupplierDto(x));
  }
}


